import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Trash2, Plus, ClipboardList } from "lucide-react";
import { ALL_LANGUAGES } from "../data/languages";
import { DISCIPLINE_CODES } from "../data/disciplines";

export default function Sessions({ user, activePatient }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    patient_initials: "",
    patient_language: "Spanish",
    notes: "",
    discipline: user.discipline,
    patient_id: null,
  });

  // Prefill from active patient when dialog opens or active patient changes
  useEffect(() => {
    if (activePatient && open) {
      setForm((f) => ({
        ...f,
        patient_initials: activePatient.initials,
        patient_language: activePatient.preferred_language,
        discipline: activePatient.discipline || user.discipline,
        patient_id: activePatient.id,
      }));
    }
  }, [activePatient, open, user.discipline]);

  const load = async () => {
    try {
      const { data } = await api.get("/sessions");
      setItems(data);
    } catch {
      toast.error("Could not load sessions");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.patient_initials.trim() || !form.notes.trim()) {
      toast.error("Initials and notes required");
      return;
    }
    try {
      await api.post("/sessions", form);
      toast.success("Session saved");
      setOpen(false);
      setForm({ patient_initials: "", patient_language: "Spanish", notes: "", discipline: user.discipline, patient_id: null });
      load();
    } catch {
      toast.error("Could not save");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/sessions/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="sessions-pane">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-2xl tracking-tight text-zinc-900">Session log</h3>
          <p className="text-sm text-zinc-600 mt-1">Quick patient notes after each session.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-11" data-testid="new-session-button">
              <Plus className="w-4 h-4 mr-2" /> New session
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Log new session</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm uppercase tracking-[0.2em] text-zinc-500">Patient initials</Label>
                <Input
                  value={form.patient_initials}
                  onChange={(e) => setForm({ ...form, patient_initials: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="J.D."
                  data-testid="session-initials-input"
                />
              </div>
              <div>
                <Label className="text-sm uppercase tracking-[0.2em] text-zinc-500">Patient language</Label>
                <Select value={form.patient_language} onValueChange={(v) => setForm({ ...form, patient_language: v })}>
                  <SelectTrigger className="mt-2 h-11" data-testid="session-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {ALL_LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm uppercase tracking-[0.2em] text-zinc-500">Discipline</Label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
                  {DISCIPLINE_CODES.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setForm({ ...form, discipline: d })}
                      className={`h-11 rounded-lg border text-sm font-medium ${
                        form.discipline === d ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                      }`}
                      data-testid={`session-discipline-${d}`}
                    >{d}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm uppercase tracking-[0.2em] text-zinc-500">Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={5}
                  className="mt-2 resize-none"
                  placeholder="Goals worked on, response, next steps…"
                  data-testid="session-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="session-cancel-button">Cancel</Button>
              <Button onClick={save} data-testid="session-save-button">Save session</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-zinc-200 rounded-xl p-10 text-center" data-testid="sessions-empty">
          <ClipboardList className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">No sessions logged yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((s) => (
            <div key={s.id} className="border border-zinc-200 rounded-xl p-5 bg-white" data-testid={`session-item-${s.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-heading text-xl tracking-tight text-zinc-900">{s.patient_initials}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {new Date(s.created_at).toLocaleString()} · {s.discipline} · {s.patient_language}
                  </p>
                </div>
                <button
                  onClick={() => remove(s.id)}
                  className="p-2 text-zinc-400 hover:text-red-600 transition-colors"
                  data-testid={`session-delete-${s.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{s.notes}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
